import React, { useState } from 'react';
import { Box, Collapse, Paper, Stack, Typography } from '@mui/material';
import { InfoOutlined, ExpandMore } from '@mui/icons-material';
import type { PageHelpContent } from './page-help-content';
import { IS_BROKER_ADMIN } from '../config/admin-build';

const TITLE = 'How to use this page';

// This panel is INTERNAL sales guidance — hidden from external broker-admin
// accounts (they can reach the Health Dashboard pages too, but the "what to do
// with it" copy is internal-only sales strategy).

/**
 * Collapsible "how to use this page" panel for the admin dashboard's usage /
 * health pages. Gives the sales team a quick brief — what the page shows and
 * what to do with it — without cluttering the page (collapsed by default).
 * One shared component so all the pages stay consistent; the per-page copy
 * lives in ./page-help-content.ts.
 */
const PageHelp: React.FC<PageHelpContent> = ({ summary, actions, caveat }) => {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((prev) => !prev);

  // Internal-only: external broker admins don't get the sales playbook.
  if (IS_BROKER_ADMIN) return null;

  return (
    <Paper variant="outlined" data-testid="page-help" sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={TITLE}
        aria-expanded={open}
        sx={{ cursor: 'pointer', px: 2, py: 1.25, userSelect: 'none' }}
      >
        <InfoOutlined fontSize="small" color="primary" />
        <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>{TITLE}</Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: { xs: 'none', sm: 'block' } }}
        >
          for the sales team
        </Typography>
        <ExpandMore
          fontSize="small"
          sx={{
            color: 'text.secondary',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
      </Stack>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
            What you&apos;re looking at
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {summary}
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            What to do with it
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {actions.map((action, index) => (
              <Typography component="li" variant="body2" key={index} sx={{ mb: 0.5 }}>
                {action}
              </Typography>
            ))}
          </Box>
          {caveat && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Note: {caveat}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default PageHelp;
